import useVuelidate from '@vuelidate/core';
import { helpers, maxLength, minLength, numeric, required } from '@vuelidate/validators';
import type { ComputedRef, Ref } from 'vue';
import { computed } from 'vue';
import type {
  ResetPasswordFirstStepData,
  ResetPasswordSecondStepData,
  ResetPasswordStep,
  ResetPasswordThirdStepData,
} from '../types/steps';
import { emailExistsRule } from '../../../shared/validation/rules/email-exists-rule';
import { emailRule } from '~/shared/validation/rules/email-rule';
import { matchTo } from '~/shared/validation/rules/match-to';
import { passwordRule } from '~/shared/validation/rules/password-rule';

const fieldRequired = helpers
  .withMessage(({ $params }) => `${$params.label} is required`, required);

const emailRules = {
  email: {
    fieldRequired: helpers.withParams({ label: 'Email' }, fieldRequired),
    emailRule,
    emailExistsRule,
  },
};

const codeRules = {
  verificationCode: {
    required: helpers.withParams({ label: 'Verification code' }, fieldRequired),
    minLength: minLength(6),
    maxLength: maxLength(6),
    numeric,
  },
};

const passwordRules = {
  password: {
    fieldRequired: helpers.withParams({ label: 'Password' }, fieldRequired),
    minLength: helpers.withMessage('Password must be at least 8 characters', minLength(8)),
    passwordRule,
  },
  passwordConfirmation: {
    fieldRequired: helpers.withParams({ label: 'Password Confirmation' }, fieldRequired),
    sameAs: helpers.withMessage(
      'Passwords do not match',
      matchTo<{ password: string }>(siblings => siblings.password),
    ),
  },
};

export function useResetPasswordValidation(
  stage: Ref<ResetPasswordStep>,
  data: {
    emailData: ComputedRef<ResetPasswordFirstStepData>
    codeData: ComputedRef<ResetPasswordSecondStepData>
    passwordData: ComputedRef<ResetPasswordThirdStepData>
  },
) {
  const emailVuelidate = useVuelidate(emailRules, data.emailData, {
    $lazy: true,
  });

  const emailErrors = computed<{ email: string }>(() => ({
    email: emailVuelidate.value.email.$errors[0]?.$message as string || '',
  }));

  const emailHasErrors = computed(() => emailVuelidate.value.$invalid);

  const codeVuelidate = useVuelidate(codeRules, data.codeData, {
    $lazy: true,
  });

  const codeErrors = computed<{ verificationCode: string }>(() => ({
    verificationCode: codeVuelidate.value.verificationCode.$errors[0]?.$message as string || '',
  }));

  const codeHasErrors = computed(() => codeVuelidate.value.$invalid);

  const passwordVuelidate = useVuelidate(passwordRules, data.passwordData, {
    $lazy: true,
  });

  const passwordErrors = computed<{ password: string, passwordConfirmation: string }>(() => ({
    password: passwordVuelidate.value.password.$errors[0]?.$message as string || '',
    passwordConfirmation: passwordVuelidate.value.passwordConfirmation.$errors[0]?.$message as string || '',
  }));

  const passwordHasErrors = computed(() => passwordVuelidate.value.$invalid);

  const trigger = async () => {
    if (stage.value === 'first') {
      await emailVuelidate.value.$validate();

      return !emailVuelidate.value.$invalid;
    }

    if (stage.value === 'second') {
      await codeVuelidate.value.$validate();

      return !codeVuelidate.value.$invalid;
    }

    if (stage.value === 'third') {
      await passwordVuelidate.value.$validate();
    }

    return !emailVuelidate.value.$invalid && !codeVuelidate.value.$invalid && !passwordVuelidate.value.$invalid;
  };

  return {
    emailErrors,
    emailHasErrors,
    codeErrors,
    codeHasErrors,
    passwordErrors,
    passwordHasErrors,
    trigger,
  };
}
